import CustomButton from "@components/buttons/CustomButton";
import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

const EXTEND_MINUTE_OPTIONS = [30, 45, 60, 75, 90, 105, 120].map((minutes) => ({
  id: minutes,
  name: `${minutes} min`,
}));

interface ExtendNtxDeadlinesSliderProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExtendNtxDeadlinesSlider = ({
  isOpen,
  onClose,
}: ExtendNtxDeadlinesSliderProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [extendMinutes, setExtendMinutes] = useState(30);
  const [isExtending, setIsExtending] = useState(false);
  const extendOptions = useMemo(() => EXTEND_MINUTE_OPTIONS, []);

  useEffect(() => {
    if (isOpen) {
      setExtendMinutes(30);
    }
  }, [isOpen]);

  const handleClose = () => {
    setExtendMinutes(30);
    onClose();
  };

  const handleExtendDeadlines = async () => {
    try {
      setIsExtending(true);
      const response =
        await primeServices.extendNtxRampTradeDeadlines(extendMinutes);
      handleSuccess({
        message:
          response.msg ||
          `NTX ramp trade deadlines extended by ${extendMinutes} minutes successfully.`,
      });
      handleClose();
    } catch (err: any) {
      handleError({
        message:
          err?.response?.data?.msg ||
          "Failed to extend NTX ramp trade deadlines.",
      });
    } finally {
      setIsExtending(false);
    }
  };

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading="Extend NTX Trade Deadlines"
      onClose={handleClose}
      onClickCross={handleClose}
      showConfirm={false}
      isMobile={isMobile}
    >
      <div
        className={`flex flex-col gap-4 p-3 ${isMobile ? "min-h-[calc(80vh-100px)]" : ""}`}
      >
        <p className="text-text-base text-[13px] font-normal leading-5">
          Extend the event deadline for all active NTX ramp trades. This applies
          to trades currently in progress (event IDs 1, 2, 3, 5, 6, 7, 8) and
          pushes each deadline forward by the selected duration.
        </p>

        <div className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-bg-base px-3 py-3">
          <p className="text-text-subtle text-[11px] font-medium">
            How long should deadlines be extended?
          </p>
          <MerchantDropdown
            listOfValues={extendOptions}
            value={extendMinutes}
            onChange={(val: any) => setExtendMinutes(Number(val))}
            label="Select duration"
            color="bg-main-container-header"
            baseColor="bg-bg-page-body"
            borderColor="border-subtle"
            expandedBorderColor="border-button-primary"
          />
          <p className="text-text-subtle text-[11px]">
            Selected:{" "}
            <span className="text-text-base font-semibold">
              {extendMinutes} minutes
            </span>
          </p>
        </div>

        <div className="rounded-lg border border-border-subtle bg-bg-page-body px-3 py-2">
          <p className="text-text-subtle text-[11px] leading-4">
            Only authorized admins can run this action. Deadlines will be set to
            now + {extendMinutes} minutes for matching NTX trades.
          </p>
        </div>

        <CustomButton
          text={`EXTEND BY ${extendMinutes} MIN`}
          isActive={!isExtending}
          onClick={handleExtendDeadlines}
          isButtonLoading={isExtending}
        />
      </div>
    </SlidingPanel>
  );
};

export default ExtendNtxDeadlinesSlider;
